import dayjs from 'dayjs';
import 'dayjs/locale/sv';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import jwt_decode from 'jwt-decode';

import { IAuthenticationActionCreators, UserState } from 'store/redux/Authentication';
import * as AuthenticationStore from 'store/redux/Authentication';
import EntraOidcClient from 'clients/EntraOidcClient';
import { AuthError } from 'models/authError';
import { IUserInRole } from 'pages/Login';
import { LoginToken } from './customLoginService';

dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);
dayjs.locale('sv');

export interface IError {
  errorMessage: string;
  errorCode: string;
}

export interface IRefreshedAccessTokenResult {
  accessToken: string;
  expirationDate: Date;
}

export interface IAuthenticationService {
  beginMicrosoftLogin: () => Promise<void>;
  completeMicrosoftLogin: (code: string, state: string) => Promise<LoginToken>;
  logoutUser: () => Promise<void>;
  fetchUserDetails: (nativeAccessToken: string, selectedCustomer?: string) => Promise<IUserInRole>;
  getOrRenewNativeToken: () => Promise<string>;
}

export interface ITokenProvider {
  getOrRenewNativeToken(): Promise<string>;
}

var refreshTokenPromise: Promise<string> | null;
const fetchUserPromise = new Map<string, Promise<IUserInRole>>();

export const authenticationServiceFactory = (
  APIBaseUrl: String,
  actionCreators: IAuthenticationActionCreators,
  currentUserState: UserState | undefined
) => {
  function setLoggingInState() {
    actionCreators.setLoginState &&
      actionCreators.setLoginState({
        LoginState: AuthenticationStore.LoginState.LOGGING_IN,
        AccessToken: '',
        AccessTokenExpirationDate: new Date(Date.parse('1980-01-01')),
        RefreshToken: '',
        RefreshTokenExpirationDate: new Date(Date.parse('1980-01-01')),
      });
  }

  function beginMicrosoftLogin() {
    setLoggingInState();
    return new EntraOidcClient().authorizeRedirect();
  }

  async function completeMicrosoftLogin(code: string, state: string) {
    setLoggingInState();

    const entraOidcClient = EntraOidcClient.fromState(state);
    if (!entraOidcClient) {
      setLoginError();
      throw new AuthError('invalid_state', 'Invalid or expired login state');
    }

    const tokenResponse = await entraOidcClient.getToken(code);
    if (!tokenResponse.access_token) {
      setLoginError();
      throw new AuthError(
        tokenResponse.error ?? 'token_error',
        tokenResponse.error_description ?? 'Failed to obtain Microsoft access token'
      );
    }

    return acquireNativeToken(tokenResponse.access_token);
  }

  function acquireNativeToken(msAccessToken: string) {
    return fetch(APIBaseUrl + '/v1/login/staff', {
      method: 'GET',
      mode: 'cors', // no-cors, *cors, same-origin
      cache: 'no-cache',
      credentials: 'same-origin', // include, *same-origin, omit
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'bearer ' + msAccessToken,
      },
      redirect: 'error',
      referrerPolicy: 'no-referrer',
    })
      .then(ThrowAuthErrorOnErrorResponse)
      .then((response: Response) => response.json())
      .then((response: LoginToken) => {
        let nativeAccessToken = response.accessToken;
        let nativeRefreshToken = response.refreshToken;
        const tokenData: any = jwt_decode(nativeAccessToken);
        const refreshTokenData: any = jwt_decode(nativeRefreshToken);

        const state = {
          AccessToken: nativeAccessToken,
          AccessTokenExpirationDate: new Date(tokenData['exp'] * 1000),
          RefreshToken: nativeRefreshToken,
          RefreshTokenExpirationDate: new Date(refreshTokenData['exp'] * 1000),
          MfaWasUsed: true, // WTF
        };
        actionCreators.setLoginState && actionCreators.setLoginState(state);
        fetchUserDetails(nativeAccessToken).then(updateUserInRole);
        return response;
      })
      .catch((error: any) => {
        console.error(error);
        setLoginError();
        throw error;
      });
  }
  const ThrowAuthErrorOnErrorResponse = (response: Response) => {
    if (response.status === 400) {
      throw new AuthError('400', 'Bad request');
    } else if (response.status === 401) {
      throw new AuthError('401', 'Unauthorized');
    } else if (response.status === 403) {
      throw new AuthError('403', 'Forbidden');
    }
    if (response.status === 404) {
      throw new AuthError('404', 'Not found');
    } else if (response.status < 200 || response.status > 400) {
      throw new AuthError(response.status.toString(), 'Server error');
    } else {
      return response;
    }
  };
  function refreshNativeToken() {
    if (refreshTokenPromise == null) {
      refreshTokenPromise = new Promise<string>((resolve, reject) => {
        fetch(APIBaseUrl + '/v1/login/AccessToken/Refresh', {
          method: 'POST',
          mode: 'cors', // no-cors, *cors, same-origin
          cache: 'no-cache',
          credentials: 'same-origin', // include, *same-origin, omit
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'bearer ' + currentUserState?.RefreshToken,
          },
          redirect: 'error',
          referrerPolicy: 'no-referrer',
        })
          .then((response: Response) => {
            refreshTokenPromise = null;
            if (response.status >= 200 && response.status < 300) {
              response.json().then((result: IRefreshedAccessTokenResult) => {
                var tokenData: any = jwt_decode(result.accessToken);
                var refreshedState = {
                  AccessToken: result.accessToken,
                  AccessTokenExpirationDate: new Date(tokenData['exp'] * 1000),
                  MfaWasUsed: false,
                };
                actionCreators.setLoginState && actionCreators.setLoginState(refreshedState);
                resolve(result.accessToken);
              });
            } else {
              if (response.status === 400) {
                reject(new AuthError('400', 'Bad request'));
              } else if (response.status === 401) {
                reject(new AuthError('401', 'Unauthorized'));
              } else if (response.status === 403) {
                reject(new AuthError('403', 'Forbidden'));
              } else {
                reject(new AuthError(response.status.toString(), 'Server error'));
              }
            }
          })
          .catch((error: any) => {
            refreshTokenPromise = null;
            if (error.message && error.message === 'Failed to fetch') {
              reject(new AuthError('-1', 'Could not find server'));
            } else {
              reject({ errorCode: -1, errorMessage: 'Unexpected Error' });
            }
          });
      });
    }
    return refreshTokenPromise;
  }
  function fetchUserDetails(nativeAccessToken: string, selectedCustomer?: string) {
    let url: string = APIBaseUrl + '/v1/user/customer';
    var customer = 'none';
    if (selectedCustomer) {
      url += '?specificCustomerId=' + selectedCustomer;
      customer = selectedCustomer;
    }

    if (fetchUserPromise.has(customer)) {
      return fetchUserPromise.get(customer) as Promise<IUserInRole>;
    }

    var promise = new Promise<IUserInRole>((resolve, reject) => {
      fetch(url, {
        method: 'GET',
        mode: 'cors', // no-cors, *cors, same-origin
        cache: 'no-cache',
        credentials: 'same-origin', // include, *same-origin, omit
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'bearer ' + nativeAccessToken,
        },
        redirect: 'error',
        referrerPolicy: 'no-referrer',
      })
        .then((response: Response) => {
          fetchUserPromise.delete(customer);
          if (response.status >= 200 && response.status < 300) {
            response.json().then((responseAsJson: IUserInRole) => {
              if (responseAsJson.availableCustomers) {
                responseAsJson.availableCustomers = responseAsJson.availableCustomers.sort(
                  (a, b) =>
                    a.customerName < b.customerName ? -1 : a.customerName > b.customerName ? 1 : 0
                );
                if (responseAsJson.availableCustomers.length === 1) {
                  responseAsJson.selectedCustomer = responseAsJson.availableCustomers[0];
                } else if (selectedCustomer) {
                  const requestedCustomer = responseAsJson.availableCustomers.find(
                    (c) => c.customerId.toString() === selectedCustomer
                  );
                  responseAsJson.selectedCustomer = requestedCustomer;
                }
              }
              resolve(responseAsJson);
            });
          } else {
            if (response.status === 400) {
              reject({ errorMessage: 'Bad request', errorCode: 400 });
            } else if (response.status === 401) {
              reject({ errorMessage: 'Unauthorized', errorCode: 401 });
            } else if (response.status === 403) {
              reject({ errorMessage: 'Forbidden', errorCode: 403 });
            } else {
              reject({
                errorMessage: 'Server Error',
                errorCode: response.status,
              });
            }
          }
        })
        .catch((error: any) => {
          fetchUserPromise.delete(customer);
          console.warn('Could not fetch user details');
          reject(error);
        });
    });
    fetchUserPromise.set(customer, promise);

    return promise;
  }
  function getOrRenewNativeToken(): Promise<string> {
    var _nativeAccessToken: string = '';

    return new Promise((resolve, reject) => {
      console.debug('Getting token from cache.');
      if (
        currentUserState &&
        currentUserState.AccessTokenExpirationDate &&
        currentUserState.AccessTokenExpirationDate > new Date() &&
        currentUserState.AccessToken
      ) {
        console.debug('Using cached access token');
        resolve(currentUserState.AccessToken);
        return;
      }

      if (
        !currentUserState ||
        !currentUserState.RefreshToken ||
        !currentUserState.RefreshTokenExpirationDate ||
        currentUserState.RefreshTokenExpirationDate < new Date()
      ) {
        console.debug('Both cached access token and refresh token was outdated or missing.');
        logoutUser().then(() => {
          throw 'AccessToken_Expired';
        });
        reject();
        return;
      }

      console.debug(
        'Cached token was outdated, using valid refresh token to refresh access token.',
        currentUserState.AccessTokenExpirationDate
      );
      refreshNativeToken()
        .then((nativeAccessToken: string) => {
          console.debug('Access token refresh succeeded');
          var promise = fetchUserDetails(nativeAccessToken);
          promise
            .then(() => {
              var tokenData: any = jwt_decode(nativeAccessToken);
              actionCreators.setLoginState &&
                actionCreators.setLoginState({
                  AccessToken: nativeAccessToken,
                  AccessTokenExpirationDate: new Date(tokenData['exp'] * 1000),
                });
              _nativeAccessToken = nativeAccessToken;
              resolve(_nativeAccessToken);
            })
            .catch((error: any) => {
              console.debug('Could not fetch user details. Error: ' + JSON.stringify(error), error);
              actionCreators.setLoginState &&
                actionCreators.setLoginState({
                  LoginState: AuthenticationStore.LoginState.LOGIN_FAILED,
                  UserInRole: undefined,
                  AccessToken: '',
                });
            });
          return promise;
        })
        .then((userInRole: IUserInRole) => {
          console.debug('New user details were fetched.', userInRole);
          userInRole.selectedCustomer = currentUserState?.UserInRole?.selectedCustomer;
          actionCreators.setLoginState &&
            actionCreators.setLoginState({
              LoginState: AuthenticationStore.LoginState.LOGGED_IN,
              UserInRole: userInRole,
            });
          resolve(_nativeAccessToken);
        });
    });
  }
  function updateUserInRole(userInRole: IUserInRole) {
    if (userInRole.availableCustomers) {
      userInRole.availableCustomers = userInRole.availableCustomers.sort((a, b) =>
        a.customerName < b.customerName ? -1 : a.customerName > b.customerName ? 1 : 0
      );
    }
    actionCreators.setLoginState &&
      actionCreators.setLoginState({
        LoginState: AuthenticationStore.LoginState.LOGGED_IN,
        UserInRole: userInRole,
      });
  }
  function setLoginError() {
    actionCreators.setLoginState &&
      actionCreators.setLoginState({
        LoginState: AuthenticationStore.LoginState.LOGIN_FAILED,
        UserInRole: undefined,
        AccessToken: '',
      });
  }
  function logoutUser() {
    return new Promise<void>((resolve, reject) => {
      actionCreators.setLoginState &&
        actionCreators.setLoginState({
          LoginState: AuthenticationStore.LoginState.NOT_LOGGED_IN,
          UserInRole: undefined,
          AccessToken: '',
          RefreshToken: '',
          AccessTokenExpirationDate: new Date(Date.parse('1970-01-02')),
          RefreshTokenExpirationDate: new Date(Date.parse('1970-01-02')),
          MfaWasUsed: false,
        });
      resolve();
    });
  }

  return {
    beginMicrosoftLogin: beginMicrosoftLogin,
    completeMicrosoftLogin: completeMicrosoftLogin,
    logoutUser: logoutUser,
    fetchUserDetails: fetchUserDetails,
    getOrRenewNativeToken: getOrRenewNativeToken,
  } as IAuthenticationService;
};
